import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Printer, FileDown } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { money, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pills } from "@/components/admin/ReceiptForm";
import { receiptWhen } from "@/lib/receipt";
import { periodRange, shiftRef, totalsByType, printTotals, downloadTotalsPDF } from "@/lib/receiptTotals";

const KINDS = [{ value: "week", label: "Weekly" }, { value: "month", label: "Monthly" }, { value: "year", label: "Yearly" }];

export function ReceiptTotals({ dest }) {
  const [kind, setKind] = useState("month");
  const [ref, setRef] = useState(todayISO());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const p = periodRange(kind, ref);
  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get("/receipts", { params: { destination: dest, start: p.start, end: p.end } }).then((r) => { if (live) { setRows([...r.data].sort((a, b) => a.date.localeCompare(b.date) || a.seq - b.seq)); setLoading(false); } }).catch((e) => { if (live) toast.error(errMsg(e)); });
    return () => { live = false; };
  }, [dest, p.start, p.end]);
  const types = totalsByType(rows);
  const grand = rows.reduce((a, r) => a + (r.total || 0), 0);
  const rep = { dest, label: p.label, rows };
  return (
    <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="receipt-totals">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Pills value={kind} onChange={setKind} options={KINDS} testid="totals-period" />
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => printTotals(rep)} disabled={loading} data-testid="totals-print-btn"><Printer className="mr-2 h-4 w-4" /> Print</Button>
          <Button variant="outline" onClick={() => downloadTotalsPDF(rep)} disabled={loading} data-testid="totals-pdf-btn"><FileDown className="mr-2 h-4 w-4" /> Download PDF</Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setRef(shiftRef(kind, ref, -1))} className="rounded-md border border-slate-300 p-2 hover:bg-slate-50" aria-label="Previous period" data-testid="totals-prev"><ChevronLeft className="h-4 w-4" /></button>
        <p className="min-w-[14rem] text-center font-heading text-lg font-bold text-slate-900" data-testid="totals-label">{p.label}</p>
        <button type="button" onClick={() => setRef(shiftRef(kind, ref, 1))} className="rounded-md border border-slate-300 p-2 hover:bg-slate-50" aria-label="Next period" data-testid="totals-next"><ChevronRight className="h-4 w-4" /></button>
        <Input type="date" value={ref} onChange={(e) => e.target.value && setRef(e.target.value)} className="h-10 w-44 bg-white text-slate-900" aria-label="Pick a date in the period" data-testid="totals-date" />
      </div>
      <div className={`grid gap-3 transition-opacity sm:grid-cols-2 lg:grid-cols-5 ${loading ? "opacity-40" : ""}`} data-testid="totals-cards">
        {types.map((t) => (
          <div key={t.key} className="rounded-lg border border-slate-200 p-3" data-testid={`totals-type-${t.key}`}>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t.label}</p>
            <p className="font-heading text-xl font-bold text-slate-900">{money(t.total)}</p>
            <p className="text-xs text-slate-500">{t.count} receipt(s)</p>
          </div>
        ))}
        <div className="rounded-lg bg-[hsl(var(--primary))] p-3 text-white" data-testid="totals-grand">
          <p className="text-xs font-semibold uppercase tracking-wider opacity-80">Grand total</p>
          <p className="font-heading text-xl font-bold">{money(grand)}</p>
          <p className="text-xs opacity-80">{rows.length} receipt(s)</p>
        </div>
      </div>
      <div className={`overflow-x-auto transition-opacity ${loading ? "opacity-40" : ""}`}>
        <table className="w-full text-sm" data-testid="totals-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["No.", "Date", "Paid by", "Church", "For", "Event", "Total"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>
            {!rows.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500" data-testid="totals-empty">No receipts in this period.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`totals-row-${r.number}`}>
                <td className="px-3 py-2 font-bold">{r.number}</td><td className="px-3 py-2">{receiptWhen(r)}</td><td className="px-3 py-2">{r.payer_name}</td><td className="px-3 py-2">{r.church_name}</td>
                <td className="px-3 py-2">{r.payment_label}</td><td className="px-3 py-2">{r.event_name || "—"}</td><td className="px-3 py-2 text-right font-semibold">{money(r.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
